import * as Sentry from "@sentry/react";

// Recovery for a stale deploy. Every merge to main redeploys and renames the
// hashed route chunks; a tab opened before the deploy still holds the old
// index.html, so the next route it visits asks for a chunk that no longer
// exists (Vercel answers with index.html, which the browser refuses as a
// module). React.lazy remembers the failure, so only a reload can recover.
// ErrorBoundary uses these to reload once, at the same URL, instead of
// showing the error screen.

// Chrome, Firefox and Safari wording for a dynamic import() that could not be
// fetched or came back as HTML, plus Vite's own CSS-preload error.
const CHUNK_LOAD_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|not a valid JavaScript MIME type|Failed to load module script/i;

export const isChunkLoadError = (err: unknown): err is Error =>
  err instanceof Error && CHUNK_LOAD_RE.test(err.message);

const RELOAD_KEY = "cello_chunk_reload_at";
// After a successful reload every chunk in the fresh index.html exists, so a
// second failure this soon is a real outage: let the error screen show.
const RELOAD_WINDOW_MS = 30_000;

/** sessionStorage throws in private mode and with site data blocked. */
const readReloadAt = (): number | null => {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
};

/** True when this tab hasn't auto-reloaded for a stale chunk in the last 30 s. Pure read, safe in render. */
export const canAutoReload = (): boolean => {
  try {
    const at = readReloadAt();
    return at === null || !Number.isFinite(at) || Date.now() - at > RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
};

/**
 * Record the reload, report one low-severity event so the frequency stays countable,
 * and reload at the same URL (path, query and hash kept). Returns false when the
 * reload could not be recorded (storage blocked), so the caller never loops.
 */
export const reloadOnceForChunkError = (err: Error): boolean => {
  if (!canAutoReload()) return false;
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    if (sessionStorage.getItem(RELOAD_KEY) === null) return false;
  } catch {
    return false;
  }
  Sentry.captureMessage("Stale chunk: auto reload", {
    level: "info",
    tags: { chunk_reload: "auto" },
    fingerprint: ["chunk-reload"],
    extra: { message: err.message, url: window.location.href },
  });
  window.location.reload();
  return true;
};
